import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { updateBreadcrumbs } from '../../app/actions/UpdateBreadcrumbsAction';
import { appStore } from '../../app/AppStore';
import { watchUrlForBreadcrumbUpdates } from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { BreadcrumbStateInfo } from '../../app/content/breadcrumbs/BreadcrumbStateInfo';
import { ServerUsersGrid } from '../../app/content/ServerUsersGrid';
import { IMainScope } from '../../app/IMainScope';
import { IPlaceTitleScope } from '../../app/IPlaceTitleScope';
import { ServerUrls } from '../../app/ServerUrls';
import { detachSorterOnPathChange } from '../../app/spaces/SpaceConfig/SpaceConfigHelper';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { ListResult } from '../fetchers/ListResult';
import { ISiteRoleFilter } from '../filters/SiteRoleFilter';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { ActionContext } from '../helpers/ActionContext';
import { SiteRoleFilterHelper } from '../helpers/SiteRoleFilterHelper';
import { ServerUserContentType } from '../models/ContentTypes';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { ITabsBannerModel } from '../models/ITabsBannerModel';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResourceQuery } from '../resources/ResourceQuery';
import { ServerUsers } from '../resources/ServerUsers';
import { SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { ILicenseConsumptionInfo } from '../ServerApi/types/ILicenseConsumptionInfo';
import { IActionButton, IListActionButton } from '../services/ActionPanelService';
import { AddUsersOptionsAction, UserAddedEvent } from '../services/AddUsersOptionsAction';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { FilterPanelService, ISelectFilterDefinition } from '../services/FilterPanelService';
import { LicensesService } from '../services/LicensesService';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ServerService } from '../services/ServerService';
import { ServerUsersActions } from '../services/ServerUsersActions';
import { SorterFactory } from '../services/SorterFactory';
import { ListActionsScope, SubplaceWithDataGridScope } from './ContentSubplaceCtrl';
import { IFilterPanelContainerScope } from './FilterPanelCtrl';
import { SiteRolesEvent } from './SiteMembershipDialogCtrl';

export interface IServerUsersPlaceScope
extends IMainScope,
        IActionsMenuScope<IServerUserInfo, {}>,
        IPlaceTitleScope,
        ListActionsScope,
        RefreshContentScope,
        SubplaceWithActionButtonsScope<IServerUserInfo, {}>,
        SubplaceWithDataGridScope<IServerUserInfo>,
        IFilterPanelContainerScope {
  model: ITabsBannerModel;
  server: ServerService;
  shouldShowSubscriptionLicensingInfoBar: boolean;
  licenseConsumptionInfo: ILicenseConsumptionInfo;
  isFetchingLicenseInfo: boolean;
  isCoreBasedLicense: boolean;
}

export function getServerUsersPlaceBreadcrumb(): BreadcrumbStateInfo {
  const serverUrls = ServerUrls.getInstance();
  return {
    id: serverUrls.usersUrl(),
    text: Messages.allServerUsers(),
    href: serverUrls.usersUrl(),
    testId: 'server-users-link'
  };
}

export class ServerUsersPlaceCtrl {

  public static $inject = [
    '$location',
    '$scope',
    '$translate',
    'AngularToReactDataGridBridge',
    'siteRoleFilter',
    'AddUsersOptionsAction',
    'BrowserTitleService',
    'QueryService',
    'ServerService',
    'LicensesService',
    'ServerUsers',
    'SiteRoles',
    'ServerUsersActions',
    'SorterFactory',
    'FilterPanelService',
    'SiteRoleOptionsResource'
  ];

  private maxSiteRoleFilterDefinition: ISelectFilterDefinition<string>;

  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'usersEmptyMessage' };

  constructor(
    private $location: ng.ILocationService,
    private $scope: IServerUsersPlaceScope,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    private siteRoleFilter: ISiteRoleFilter,
            addUsersOptionsAction: AddUsersOptionsAction,
            BrowserTitleService: BrowserTitleService,
            queryService: QueryService,
    private serverService: ServerService,
    private licensesService: LicensesService,
    private users: ServerUsers,
    private siteRoles: SiteRolesResource,
            serverUsersActions: ServerUsersActions,
            sorterFactory: SorterFactory,
            filterPanelService: FilterPanelService,
    private siteRoleOptionsResource: SiteRoleOptionsResource
  ) {

    const selectedItems = new Set<IServerUserInfo>((user: IServerUserInfo) => user.id);
    const actions = serverUsersActions.getServerUsersActionsButtons();
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, selectedItems);

    this.configureForReactDataGrid(actions, getActionTarget);

    BrowserTitleService.setTitleWithPlace({translatablePlaceName: 'serverUsers'});

    $scope.server = serverService;

    $scope.isFetchingLicenseInfo = false;
    $scope.shouldShowSubscriptionLicensingInfoBar = true;

    $scope.selectedItems = selectedItems;

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    $scope.listActions = [{
      name: 'add-users',
      translate: 'addUsers',
      isAvailable: () => true,
      execute: ($event: JQueryEventObject) => {
        addUsersOptionsAction.showAddServerUsersOptionsDialog(ActionContext.fromEvent($event));
      }
    }];
    $scope.availableListAction = (listAction: IListActionButton) => listAction.isAvailable();

    $scope.filterControls = {};

    $scope.filterDefinitions = filterPanelService.getFilterDefinitions([ 'maxSiteRole' ]);
    this.maxSiteRoleFilterDefinition = <ISelectFilterDefinition<string>>$scope.filterDefinitions[0];

    $scope.sorter = sorterFactory.create(ServerUserContentType.defaultOrder);

    const queryUpdater: IQueryUpdater =
      queryService.createQueryUpdater(
        $scope,
        ServerUserContentType,
        users,
        (resourceQuery) => {
          this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: this.$scope, sliceable: resourceQuery });
          $scope.sliceable = resourceQuery;
          $scope.sorter.setOrder(resourceQuery.getOrder());
          this.updateMaxSiteRoleCounts(resourceQuery);
          resourceQuery.getCount().then((result: number) => {
            this.$scope.model.itemsCount = result;
          });
        },
        {}
      );

    $scope.sliceChanged = (newSlice: ListResult<IServerUserInfo>) => {
      $scope.selectedItems.updateItems(newSlice.items);
      $scope.model.itemsCount = newSlice.total;
    };

    $scope.refreshContent = () => {
      queryUpdater.refresh();
      this.updateLicenseConsumptionInfo();
    };

    $scope.refreshContent();

    $scope.placeTitleClicked = () => {
      $scope.refreshContent();
    };

    $scope.$on(UserAddedEvent, () => {
      $scope.refreshContent();
    });

    $scope.$on(SiteRolesEvent.SiteRolesChanged, () => {
      $scope.refreshContent();
    });

    $scope.model = { itemsCount: 0 };

    $scope.emptyTemplateModel = ServerUsersPlaceCtrl.emptyTemplateModel;

    this.initBreadcrumbs();
    const currentPath = this.$location.path();
    const shouldDetach = () => this.$location.path() !== currentPath;
    detachSorterOnPathChange(this.$scope, this.$location, shouldDetach);
  }

  private configureForReactDataGrid(actions: IActionButton<IServerUserInfo, {}>[],
                                    getActionTarget: () => IActionTarget<IServerUserInfo, {}>) {

    const columns = ServerUsersGrid.columns(
      (id: string) => this.$translate.instant(id),
      this.$scope,
      ServerUrls.getInstance(),
      this.siteRoleFilter,
      actions,
      getActionTarget,
      this.serverService.isServerDomainFamilyActiveDirectory(),
      this.serverService.shouldShowServerLicensingTierInfo());

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(ServerUsersPlaceCtrl.emptyTemplateModel),
      canShowFilterPanel: ServerUserContentType.canShowFilterPanel,
      headerHeight: CommonStyles.Sizes.DataGridWithPictureIconsHeaderHeight,
      rowOuterHeight: CommonStyles.Sizes.DataGridWithPictureIconsRowHeight
    });
  }

  private updateMaxSiteRoleCounts(baseQuery: IResourceQuery<IServerUserInfo>) {
    this.users.getUserCountsByMaxSiteRole(baseQuery).then((userCountsByRole: _.Dictionary<number>) => {
      this.maxSiteRoleFilterDefinition.options =
        SiteRoleFilterHelper.siteRoleOptions(this.siteRoles.optionsForCurrentUser(), this.siteRoleOptionsResource, userCountsByRole);
    });
  }

  private updateLicenseConsumptionInfo = () => {
    if (!this.$scope.shouldShowSubscriptionLicensingInfoBar) {
      return;
    }

    this.$scope.isFetchingLicenseInfo = true;
    this.licensesService.fetchCapacityInfoForInfoBar().then((licenseInfo: ILicenseConsumptionInfo) => {
      this.$scope.licenseConsumptionInfo = licenseInfo;
      this.$scope.isCoreBasedLicense = licenseInfo.totalCoreLicenses > 0;
    }).finally(() => this.$scope.isFetchingLicenseInfo = false);
  }

  private initBreadcrumbs() {
    const breadcrumbs: BreadcrumbStateInfo[] = [
      getServerUsersPlaceBreadcrumb()
    ];
    appStore.dispatch(updateBreadcrumbs(breadcrumbs));

    const currentPath = this.$location.path();
    watchUrlForBreadcrumbUpdates(this.$scope, this.$location, () => this.$location.path() === currentPath);
  }
}

Module.instance().controller('ServerUsersPlaceCtrl', ServerUsersPlaceCtrl);
